import { useId } from 'react';
import { FormattedMessage } from 'react-intl';
import type { Season } from '@tg/shared';
import { useFormat } from '@/i18n/useFormat';
import { lanternMarkup } from '@/fx/festive';
import ss from './season.module.css';

const LANTERNS = [0, 1, 2] as const;

/** Row of swaying lanterns (decorative; still under reduced motion). */
export function SeasonGarland({ compact }: { compact?: boolean }) {
  const uid = useId().replace(/[^\w-]/g, '');
  return (
    <span
      className={compact ? `${ss.garland} ${ss.garlandSm}` : ss.garland}
      aria-hidden="true"
      data-testid="season-garland"
    >
      {LANTERNS.map((i) => (
        <svg
          key={i}
          className={ss.lantern}
          viewBox="0 0 128 128"
          style={{ animationDelay: `${i * 0.4}s` }}
          dangerouslySetInnerHTML={{ __html: lanternMarkup(`sl${uid}${i}`) }}
        />
      ))}
    </span>
  );
}

/** Lobby banner of the current holiday (text i18n, end date in Beijing time). */
export function SeasonBanner({ season }: { season: Season }) {
  const f = useFormat();
  const titleId = useId();
  const to = f.date(`${season.to}T12:00:00+08:00`, {
    day: 'numeric',
    month: 'long',
    timeZone: 'Asia/Shanghai',
  });
  return (
    <section className={ss.banner} data-season={season.id} aria-labelledby={titleId}>
      <div className={ss.text}>
        <h2 id={titleId} className={ss.title}>
          <FormattedMessage id={`lobby.season.${season.id}.title`} />
        </h2>
        <p className={ss.sub}>
          <FormattedMessage id={`lobby.season.${season.id}.text`} values={{ to }} />
        </p>
        <p className={ss.note}>
          <FormattedMessage id="lobby.season.note" />
        </p>
      </div>
      <SeasonGarland />
    </section>
  );
}
